import type { I18n } from '@/lib/api/types';
import { arCompleteness } from '@/lib/i18n/content';

/**
 * Page-copy content is a free-form JSONB tree whose *leaves* are bilingual `{ en, ar }`
 * I18n objects (the public `/page-copy/{ns}` endpoint resolves each leaf to one locale).
 * These pure helpers let the editor render that tree as paired EN/AR fields instead of raw
 * JSON, track Arabic coverage, and emit a minimal RFC 7396 Merge Patch for the PATCH route.
 */

export type Json =
  | string
  | number
  | boolean
  | null
  | Json[]
  | { [key: string]: Json };

export type NodeKind = 'i18n' | 'image' | 'group' | 'list' | 'text' | 'number' | 'boolean' | 'null';

/** Path from the content root: object keys (string) and array indices (number). */
export type Path = ReadonlyArray<string | number>;

export function isPlainObject(v: unknown): v is Record<string, Json> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

/** A leaf is an `{ en, ar }` pair: a plain object keyed only by `en`/`ar` with string values. */
export function isI18nLeaf(v: unknown): v is I18n {
  if (!isPlainObject(v)) return false;
  const keys = Object.keys(v);
  if (keys.length === 0) return false;
  if (!keys.every((k) => k === 'en' || k === 'ar')) return false;
  return keys.every((k) => typeof v[k] === 'string');
}

export function asI18n(v: unknown): I18n {
  if (isI18nLeaf(v)) return { en: v.en ?? '', ar: v.ar ?? '' };
  return { en: '', ar: '' };
}

/** Keys an image node may carry beside `image_id` (`image` is the public read's hydration). */
const IMAGE_NODE_KEYS = new Set(['image_id', 'alt', 'width', 'height', 'image']);

/**
 * An image node: an object with an `image_id` key whose other keys are a subset of
 * {alt, width, height, image}. Detection is strict on `image_id` so an object that merely
 * has some other `*_id` / `id` key keeps rendering as an ordinary group.
 */
export function isImageNode(v: unknown): v is Record<string, Json> {
  if (!isPlainObject(v) || !('image_id' in v)) return false;
  const id = v.image_id;
  if (id !== null && typeof id !== 'string') return false;
  return Object.keys(v).every((k) => IMAGE_NODE_KEYS.has(k));
}

export function classify(v: Json): NodeKind {
  if (v === null) return 'null';
  if (Array.isArray(v)) return 'list';
  if (isI18nLeaf(v)) return 'i18n';
  if (isImageNode(v)) return 'image';
  if (isPlainObject(v)) return 'group';
  if (typeof v === 'number') return 'number';
  if (typeof v === 'boolean') return 'boolean';
  return 'text';
}

// ── Arabic-coverage accounting ────────────────────────────────────────────────
export interface Coverage {
  /** I18n leaves that have English content (the translatable surface). */
  total: number;
  /** …of those, how many also have Arabic content. */
  translated: number;
}

/**
 * Page-copy documents come in two shapes:
 *   • **locale-first** — `{ en: {…tree…}, ar: {…tree…} }` — two parallel trees of plain-string
 *     leaves (this is what the public `/page-copy/{ns}` endpoint stores for most namespaces);
 *   • **bilingual-leaf** — `{ field: { en, ar }, … }` — the same `{en,ar}` leaves used across
 *     the rest of the console.
 *
 * The earlier implementation only understood the bilingual-leaf shape via {@link arCompleteness},
 * so locale-first namespaces (e.g. `hero`) reported "no translatable fields" even when fully
 * populated (FE-SETTINGS-1). We now detect the locale-first shape and count its EN leaves against
 * their AR counterparts; everything else falls back to the canonical leaf accounting so the
 * numbers still match the app-wide "missing Arabic" filter.
 */
export function coverage(node: Json): Coverage {
  if (isLocaleFirst(node)) return localeFirstCoverage(node);
  const { total, missing } = arCompleteness(node);
  return { total, translated: total - missing };
}

/** Locale-first: an object keyed only by `en`/`ar` whose values are nested trees, not strings. */
export function isLocaleFirst(node: Json): node is Record<'en' | 'ar', Json> {
  if (!isPlainObject(node)) return false;
  const keys = Object.keys(node);
  if (keys.length === 0 || !keys.every((k) => k === 'en' || k === 'ar')) return false;
  // A single bilingual leaf `{en:'x', ar:'y'}` has string values — that's NOT locale-first.
  return keys.some((k) => isPlainObject(node[k]) || Array.isArray(node[k]));
}

/** Count EN string leaves (the translatable surface) against the AR leaf at the same path. */
function localeFirstCoverage(node: Record<'en' | 'ar', Json>): Coverage {
  let total = 0;
  let translated = 0;
  const walk = (en: Json, ar: Json | undefined): void => {
    if (typeof en === 'string') {
      if (en.trim()) {
        total += 1;
        if (typeof ar === 'string' && ar.trim()) translated += 1;
      }
      return;
    }
    if (Array.isArray(en)) {
      en.forEach((child, i) => walk(child, Array.isArray(ar) ? ar[i] : undefined));
    } else if (isPlainObject(en)) {
      for (const [k, child] of Object.entries(en)) {
        walk(child, isPlainObject(ar) ? ar[k] : undefined);
      }
    }
  };
  if (node.en !== undefined) walk(node.en, node.ar);
  return { total, translated };
}

/** Whole-tree Arabic completeness — true once every translatable leaf has Arabic. */
export function isArComplete(node: Json): boolean {
  const c = coverage(node);
  return c.total > 0 && c.translated === c.total;
}

// ── Immutable tree edits ──────────────────────────────────────────────────────
/**
 * Keys a path may never step through. Copy JSON comes from `JSON.parse`, which
 * makes `"__proto__"` an ordinary own key, and assigning through it would swap
 * the copied object's prototype instead of setting a field.
 */
const RESERVED_KEYS = new Set(['__proto__', 'constructor', 'prototype']);

/** An object key the path helpers will read or write: an own, non-reserved key. */
function isEditableKey(key: string): boolean {
  return !RESERVED_KEYS.has(key);
}

function cloneShallow(v: Json): Json {
  if (Array.isArray(v)) return v.slice();
  if (isPlainObject(v)) return { ...v };
  return v;
}

/** Return a copy of `root` with `value` set at `path` (creating nothing beyond the path). */
export function setAtPath(root: Json, path: Path, value: Json): Json {
  if (path.length === 0) return value;
  const [head, ...rest] = path;
  const next = cloneShallow(root);
  if (Array.isArray(next) && typeof head === 'number') {
    next[head] = setAtPath(next[head] ?? null, rest, value);
  } else if (isPlainObject(next) && typeof head === 'string' && isEditableKey(head)) {
    next[head] = setAtPath(Object.hasOwn(next, head) ? next[head] : null, rest, value);
  }
  return next;
}

/** Return a copy of `root` with the entry at `path` removed (object key or array index). */
export function deleteAtPath(root: Json, path: Path): Json {
  if (path.length === 0) return root;
  const [head, ...rest] = path;
  const next = cloneShallow(root);
  if (rest.length === 0) {
    if (Array.isArray(next) && typeof head === 'number') next.splice(head, 1);
    else if (isPlainObject(next) && typeof head === 'string' && isEditableKey(head)) delete next[head];
    return next;
  }
  if (Array.isArray(next) && typeof head === 'number') {
    next[head] = deleteAtPath(next[head], rest);
  } else if (isPlainObject(next) && typeof head === 'string' && isEditableKey(head) && Object.hasOwn(next, head)) {
    next[head] = deleteAtPath(next[head], rest);
  }
  return next;
}

/** Read the value at `path`, or `undefined` if the path doesn't resolve. */
export function getAtPath(root: Json, path: Path): Json | undefined {
  return path.reduce<Json | undefined>((cur, seg) => {
    if (Array.isArray(cur) && typeof seg === 'number') return cur[seg];
    if (isPlainObject(cur) && typeof seg === 'string' && isEditableKey(seg) && Object.hasOwn(cur, seg)) {
      return cur[seg];
    }
    return undefined;
  }, root);
}

/** Rename a key inside an object while preserving insertion order. No-op on collisions. */
export function renameKey(
  obj: Record<string, Json>,
  oldKey: string,
  newKey: string,
): Record<string, Json> {
  if (oldKey === newKey) return obj;
  if (!isEditableKey(newKey) || Object.hasOwn(obj, newKey)) return obj; // reserved, or would clobber a sibling
  const next: Record<string, Json> = {};
  for (const [k, v] of Object.entries(obj)) next[k === oldKey ? newKey : k] = v;
  return next;
}

/** Move an array item from `index` by `delta` (clamped). Returns a new array. */
export function moveItem(list: Json[], index: number, delta: number): Json[] {
  const to = index + delta;
  if (to < 0 || to >= list.length) return list;
  const next = list.slice();
  const [item] = next.splice(index, 1);
  next.splice(to, 0, item);
  return next;
}

// ── Field paths (deep-link addressing) ────────────────────────────────────────
/**
 * Serialize a content path to dot notation, e.g. `["cta","heading"]` → `"cta.heading"`,
 * `["links",0,"label"]` → `"links.0.label"`. This is the canonical `field_path` the global
 * search uses to deep-link to one field. Object keys are slugs and array steps are numeric,
 * so the encoding round-trips for all real page-copy shapes.
 */
export function pathToString(path: Path): string {
  return path.map(String).join('.');
}

export function pathFromString(s: string): Path {
  if (!s) return [];
  return s
    .split('.')
    .filter((seg) => seg.length > 0)
    .map((seg) => (/^\d+$/.test(seg) ? Number(seg) : seg));
}

// ── Image nodes ───────────────────────────────────────────────────────────────
export type Locale = 'en' | 'ar';

/**
 * Every tree path holding the same logical node: for locale-first content the node at
 * `en.<p>` also lives at `ar.<p>`, so both are returned (EN first). A path outside a locale
 * root (bilingual-leaf content) is its own single location.
 */
export function localePaths(path: Path): Path[] {
  const [head, ...rest] = path;
  if (head === 'en' || head === 'ar') return [['en', ...rest], ['ar', ...rest]];
  return [path];
}

/** The fields of a picked asset an image node records. */
export interface PickedImage {
  id: string;
  width?: number;
  height?: number;
}

/**
 * Write a picked image into the image node at `path` **and** its other-locale twin in one
 * pass, so a single state update moves both trees together and the EN/AR parity check can
 * never see them disagree. `image_id`, `width` and `height` are written; a stale hydrated
 * `image` is dropped (it described the previous asset); `alt` is per locale and untouched.
 * A twin that does not exist yet is created only where its parent container exists.
 */
export function applyImagePick(root: Json, path: Path, picked: PickedImage): Json {
  let next = root;
  for (const p of localePaths(path)) {
    const current = getAtPath(next, p);
    if (current === undefined && getAtPath(next, p.slice(0, -1)) === undefined) continue;
    const node: Record<string, Json> = isPlainObject(current) ? { ...current } : {};
    node.image_id = picked.id;
    if (picked.width && picked.height) {
      node.width = picked.width;
      node.height = picked.height;
    } else {
      delete node.width;
      delete node.height;
    }
    delete node.image;
    next = setAtPath(next, p, node);
  }
  return next;
}

/** Clear the image (both locales), keeping each locale's alt text. */
export function applyImageClear(root: Json, path: Path): Json {
  let next = root;
  for (const p of localePaths(path)) {
    const current = getAtPath(next, p);
    if (!isPlainObject(current)) continue;
    const node: Record<string, Json> = { ...current, image_id: null };
    delete node.width;
    delete node.height;
    delete node.image;
    next = setAtPath(next, p, node);
  }
  return next;
}

// ── Parity (422) error paths ──────────────────────────────────────────────────
/**
 * Parse a backend error path in dot and/or bracket notation (`rows.2.cells`, `rows[2].cells`,
 * `content.hero.image.image_id`) into a tree {@link Path}. A leading `content` segment (the
 * FieldError `field` form) is stripped; numeric segments become array indices.
 */
export function parseErrorPath(raw: string): Path {
  const segs = raw
    .replace(/\[(\d+)\]/g, '.$1')
    .split('.')
    .filter((s) => s.length > 0)
    .map((s) => (/^\d+$/.test(s) ? Number(s) : s));
  return segs[0] === 'content' ? segs.slice(1) : segs;
}

/** A parity violation as returned in the 422 `details[]`. */
export interface ParityDetail {
  field?: string;
  path?: string;
  reason?: string;
  message?: string;
}

/**
 * Map one parity violation onto the tree paths an editor must look at. Backend paths are
 * relative to the locale root, so for locale-first content they are re-rooted under `en`/`ar`:
 * a reason that names one side (`missing_in_ar`, `missing_ar` → AR; `missing_in_en` → EN)
 * points at that side only, every other reason at both copies.
 */
export function parityPathToTreePaths(detail: ParityDetail, content: Json): Path[] {
  const raw = detail.path ?? detail.field ?? '';
  const rel = parseErrorPath(raw);
  // An empty path is a whole-document problem (a missing locale); only the banner reports it.
  if (rel.length === 0) return [];
  if (!isLocaleFirst(content)) return [rel];
  if (rel[0] === 'en' || rel[0] === 'ar') return [rel];
  const side: Locale[] =
    detail.reason === 'missing_in_ar' || detail.reason === 'missing_ar'
      ? ['ar']
      : detail.reason === 'missing_in_en'
        ? ['en']
        : ['en', 'ar'];
  return side.map((l) => [l, ...rel]);
}

/**
 * The node an error should be shown on: the deepest prefix of `path` that exists in the tree
 * (a `missing_in_ar` path names a key the AR tree lacks, so its parent is what renders).
 */
export function nearestExisting(root: Json, path: Path): Path {
  for (let n = path.length; n > 0; n--) {
    const p = path.slice(0, n);
    if (getAtPath(root, p) !== undefined) return p;
  }
  return [];
}

// ── RFC 7396 JSON Merge Patch ─────────────────────────────────────────────────
const UNCHANGED = Symbol('unchanged');

function deepEqual(a: Json, b: Json): boolean {
  if (a === b) return true;
  if (Array.isArray(a) && Array.isArray(b)) {
    return a.length === b.length && a.every((x, i) => deepEqual(x, b[i]));
  }
  if (isPlainObject(a) && isPlainObject(b)) {
    const ka = Object.keys(a);
    const kb = Object.keys(b);
    return ka.length === kb.length && ka.every((k) => k in b && deepEqual(a[k], b[k]));
  }
  return false;
}

function diff(orig: Json, edited: Json): Json | typeof UNCHANGED {
  if (isPlainObject(orig) && isPlainObject(edited)) {
    const patch: Record<string, Json> = {};
    const keys = new Set([...Object.keys(orig), ...Object.keys(edited)]);
    for (const k of keys) {
      if (!(k in edited)) {
        patch[k] = null; // deletion
      } else if (!(k in orig)) {
        patch[k] = edited[k]; // addition (whole value)
      } else {
        const sub = diff(orig[k], edited[k]);
        if (sub !== UNCHANGED) patch[k] = sub as Json;
      }
    }
    return Object.keys(patch).length ? patch : UNCHANGED;
  }
  return deepEqual(orig, edited) ? UNCHANGED : edited;
}

/**
 * Minimal merge patch turning `orig` into `edited` (`null` deletes a key; scalars/arrays
 * replace wholesale). Returns `null` when nothing changed, so callers can skip the request.
 */
export function mergePatch(orig: Json, edited: Json): Record<string, Json> | null {
  const result = diff(orig ?? {}, edited ?? {});
  if (result === UNCHANGED) return null;
  return (isPlainObject(result) ? result : {}) as Record<string, Json>;
}

// ── Human-readable field labels ───────────────────────────────────────────────
/** Field keys that read better fully uppercased than title-cased. */
const ACRONYMS = new Set(['cta', 'url', 'seo', 'faq', 'id', 'eta', 'kpi', 'pdf', 'api']);

/**
 * Turn a raw content key into a human label for the editor, e.g. `cta_heading` → "CTA heading",
 * `heroTitle` → "Hero title", `footer-note` → "Footer note". The raw key is still shown beside
 * the label (it's what the search `field_path` addresses), so this is purely cosmetic.
 */
export function humanizeKey(key: string): string {
  const words = key
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2') // split camelCase
    .replace(/[_-]+/g, ' ') // snake_case / kebab-case → spaces
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (words.length === 0) return key;
  return words
    .map((w, i) => {
      const lower = w.toLowerCase();
      if (ACRONYMS.has(lower)) return w.toUpperCase();
      // Capitalize only the first word; keep the rest lower for a natural sentence case.
      return i === 0 ? lower.charAt(0).toUpperCase() + lower.slice(1) : lower;
    })
    .join(' ');
}
